import React, { useContext, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IItemForGetShortInfoWithStatus,
	UserRoles,
	IGetRestaurantsShort,
	ITotalPrice,
	IGetRestaurantsShortArgs,
} from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	removePrice: (value: string[]) => void;
	setRestaurantsForPrice: (value: string[]) => void;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
	selectedCustomer: Nullable<string>;
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	selectedCustomer,
	restaurantsForPrice,
	totalPrices,
	setRestaurantsForPrice,
	removePrice,
	setFetchedRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		selectedCustomer && hasAccess ? getRestaurantsShortInfo : null,
		{
			customerId: selectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);

	useEffect(() => {
		refetch();
	}, [selectedCustomer]);

	const restaurants = useMemo(() => {
		return hasAccess ? response?.data.restaurants : loggedUser.restaurants;
	}, [hasAccess, response, loggedUser]);

	useEffect(() => {
		if (response) {
			setFetchedRestaurants(response.data.restaurants);
		} else if (!hasAccess) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			setFetchedRestaurants(accessibleRestaurants);
		}
	}, [response, loggedUser, hasAccess, selectedCustomer]);

	return (
		<div className={style.container}>
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					restaurantsForPrice={restaurantsForPrice}
					selectedCustomer={selectedCustomer}
					totalPrices={totalPrices}
					removePrice={removePrice}
					setRestaurantsForPrice={setRestaurantsForPrice}
				/>
			</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
